import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoaderComponent from '@components/LoaderComponent';

import ImageSearchModal from '@features/trend-research/components/common/imageSearch/ImageSearchModal';
import ExploreDesign from '@features/trend-research/components/onboarding/components/ExploreDesign';
import TrendTopFilter from '@features/trend-research/components/onboarding/components/TrendTopFilter';
import {
	allFilterPages,
	trendResearchBrandFilterFieldList,
	trendResearchInstagramFilterFieldList
} from '@features/filter/static/filterUtil';
import FilterComponentV2 from '@features/filter/v2/FilterComponent';
import FilterTopHeader from '@features/trend-research/components/onboarding/components/FilterTopHeader';
import { useFetchOnboardingFilterOptionsMutation } from '@features/trend-research/api/onboardingApi';
import useTrendResearchFilterParams from '@features/trend-research/hooks/useTrendResearchFilterParams';
import { mapBrandTypeList, mapCategoryList } from '@features/trend-research/utils/utils';

import { clearGlobalFilter, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';
import { setTrendResearchDateRange } from '@store/actions/trend-research/TrendResearchAction';
import {
	useExploreProductsSource,
	useProductCount
} from '@store/reducers/trend-research/TrendResearchReducer';
import { Types } from '@store/types';

import { changeDateFormat, getDateRange } from '@utils/Util';

const TrendOnboarding = () => {
	const dispatch = useDispatch();
	const sourceType = useExploreProductsSource();
	const [isShowImageSearch, setIsShowImageSearch] = useState(false);
	const filterData = useSelector((store) => store.filterFieldStore);
	const [isShowFilter, setIsShowFilter] = useState(false);
	const [fetchOnboardingFilterOptions, { data, isLoading }] = useFetchOnboardingFilterOptionsMutation();

	const { requestBody } = useTrendResearchFilterParams();
	const totalElements = useProductCount();

	useEffect(() => {
		if (!data) return;

		const filterList = {
			...data,
			colorList: data?.baseColorList || [],
			aopList: data?.graphicsTypeList || [],
			userInfluencerList: data?.userNameList || [],
			fabricTypeList: data?.fabricTypeList?.map((item) => ({ ...item, constant: item.name })) || [],
			categoryList: mapCategoryList(data?.categoryList),
			brandTypeList: mapBrandTypeList(data?.brandList)
		};

		dispatch({
			type: Types.STORE_DEPENDENT_FILTER_LIST,
			payload: { responseData: filterList, filterListPage: allFilterPages.TREND_RESEARCH }
		});
	}, [data, sourceType]);

	useEffect(() => {
		if (isShowFilter) {
			fetchOnboardingFilterOptions({
				source: sourceType.toUpperCase(),
				requestBody
			});
		}
	}, [sourceType, isShowFilter, requestBody]);

	useEffect(() => {
		let { start, end } = getDateRange('oneYearBack');
		let fromDate = moment(start).format('YYYY-MM-DD');
		let toDate = moment(end).format('YYYY-MM-DD');
		let data = {
			name: 'dateRange',
			key: fromDate + ',' + toDate,
			value:
				changeDateFormat(fromDate, 'YYYY-MM-DD', 'DD MMM, YYYY') +
				' - ' +
				changeDateFormat(toDate, 'YYYY-MM-DD', 'DD MMM, YYYY'),
			identifier: 'dateRange'
		};
		dispatch(updateTextItemInGlobalFilter(data));

		return () => {
			dispatch(clearGlobalFilter());
		};
	}, []);

	useEffect(() => {
		dispatch(setTrendResearchDateRange(filterData?.dateRange?.[0]));
	}, [filterData]);

	const toggleImageSearchModal = (isShow) => {
		setIsShowImageSearch(isShow);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<section className="body-content trend-data-container bg-white">
				<div className="top-header bg-white pt-3">
					<TrendTopFilter type={'Explore'} toggleImageSearchModal={toggleImageSearchModal} />
					<FilterTopHeader
						toggleFilter={() => setIsShowFilter((prev) => !prev)}
						totalItem={totalElements}
					/>
				</div>
				<div className="explore-section border-0">
					<section className="filter-data-container filter-v3-data-container px-0">
						<FilterComponentV2
							filterPageName={allFilterPages.TREND_RESEARCH}
							fields={
								sourceType === 'INSTAGRAM'
									? trendResearchInstagramFilterFieldList
									: trendResearchBrandFilterFieldList
							}
							showFilter={isShowFilter}
							isDependentFilter={true}
						/>
						<div className={`data-table mt-3 ${isShowFilter ? 'expand' : 'not-expand'}`}>
							<ExploreDesign />
						</div>
					</section>
				</div>
			</section>
			{isShowImageSearch && (
				<ImageSearchModal onClose={() => toggleImageSearchModal(false)} sourceType={sourceType} />
			)}
		</LoaderComponent>
	);
};

export default TrendOnboarding;
